import {
  Box, Button, Flex, FormControl, FormLabel, Input,
  Switch, Text, Textarea, useColorModeValue
} from '@chakra-ui/react'
import Card from 'components/Card/Card.js'
import CardBody from 'components/Card/CardBody.js'
import CardHeader from 'components/Card/CardHeader.js'
import { useFormik } from 'formik'
import React, { useCallback, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'
import Select from 'react-select'
import { GET_WORKSHEET_BY_ID, UPDATE_WORKSHEET } from 'store/worksheets'
import { CLASS, SUBJECT } from 'variables/enumObject'
import * as Yup from 'yup'

function EditWorksheet() {
  const dispatch = useDispatch()
  const history = useHistory()
  const { id } = useParams()
  const textColor = useColorModeValue('gray.700', 'white')
  const bgColor = useColorModeValue('#FFF', '#718096')
  const hoverColor = useColorModeValue('#CBD5E0', '#4A5568')
  const selectColor = useColorModeValue('#718096', '#1A202C')

  const customStyles = {
    control: (styles) => ({
      ...styles,
      borderRadius: '15px',
      outline: '2px solid transparent',
      borderColor: '#E2E8F0',
      minHeight: '3rem',
      marginBottom: '24px'
    }),
    option: (styles, state) => ({
      ...styles,
      backgroundColor: state.isFocused ? hoverColor : state.isSelected ? selectColor : bgColor,
    }),
  }

  const validationSchema = Yup.object({
    name: Yup.string().required('บังคับกรอก')
  })

  const formik = useFormik({
    initialValues: {},
    onSubmit: (values, { setSubmitting }) => {
      const formData = new FormData()
      formData.append('name', values.name?.replace(/'/g, '\'') || '')
      formData.append('subject', values.subject?.label || values.subject || '')
      formData.append('class', values.class?.label || values.class || '')
      formData.append('description', values.description?.replace(/'/g, '\'') || '')
      formData.append('url_image', values.url_image || '')
      formData.append('status', values.status)
      formData.append('is_free', values.is_free)
      formData.append('original_price', values.original_price)
      formData.append('sale_price', values.sale_price)
      formData.append('is_new', values.is_new)
      formData.append('is_recommended', values.is_recommended)
      formData.append('is_bestseller', values.is_bestseller)
      if (values.pdf_file_upload) formData.append('pdf_file', values.pdf_file_upload)

      dispatch(UPDATE_WORKSHEET(id, formData))
        .then(() => {
          setSubmitting(false)
          history.push('/admin/worksheets')
        })
        .catch(() => setSubmitting(false))
    },
    validationSchema
  })

  const initFetch = useCallback(() => {
    dispatch(GET_WORKSHEET_BY_ID(id)).then((data) => {
      formik.setFieldValue('status', data.status === true || data.status === 'true' || data.status === 'Active')
      formik.setFieldValue('name', data.name)
      formik.setFieldValue('subject', SUBJECT.find(x => x.label === data.subject) || { label: data.subject, value: data.subject })
      formik.setFieldValue('class', CLASS.find(x => x.label === data.class) || { label: data.class, value: data.class })
      formik.setFieldValue('description', data.description)
      formik.setFieldValue('url_image', data.url_image)
      formik.setFieldValue('is_free', data.is_free === true || data.is_free === 'true')
      formik.setFieldValue('original_price', data.original_price)
      formik.setFieldValue('sale_price', data.sale_price)
      formik.setFieldValue('is_new', data.is_new === true || data.is_new === 'true')
      formik.setFieldValue('is_recommended', data.is_recommended === true || data.is_recommended === 'true')
      formik.setFieldValue('is_bestseller', data.is_bestseller === true || data.is_bestseller === 'true')
      formik.setFieldValue('pdf_file', data.pdf_file)
    })
  }, [dispatch, id])

  useEffect(() => {
    initFetch()
  }, [initFetch])

  return (
    <Flex direction='column' pt={{ base: '120px', md: '75px' }}>
      <form onSubmit={formik.handleSubmit}>
        <Card overflowX={{ sm: 'scroll', xl: 'hidden' }}>
          <CardHeader>
            <Flex justify='space-between' align='center' minHeight='60px' w='100%'>
              <Text fontSize='lg' color={textColor} fontWeight='bold'>แก้ไขใบงาน</Text>
              <Text fontSize='lg' color={textColor} fontWeight='bold'>
                <small>ปิดใช้งาน</small>
                <Switch size='lg' mx='10px' id='status' name='status' isChecked={formik.values.status}
                  onChange={e => formik.setFieldValue('status', e.target.checked)} />
                <small>เปิดใช้งาน</small>
              </Text>
            </Flex>
          </CardHeader>
          <CardBody p='20px'>
            <FormControl>
              <FormLabel ms='4px' fontSize='sm' fontWeight='normal'>ชื่อใบงาน</FormLabel>
              <Input
                fontSize='sm' ms='4px' borderRadius='15px' type='text' mb='24px' size='lg'
                name='name' onChange={formik.handleChange} onBlur={formik.handleBlur}
                value={formik.values.name || ''}
              />
              <Flex gap='5'>
                <Box flex='1'>
                  <FormLabel ms='4px' fontSize='sm' fontWeight='normal'>วิชา</FormLabel>
                  <Select
                    options={SUBJECT} id='subject' name='subject' placeholder='เลือกวิชา'
                    styles={customStyles} value={formik.values.subject}
                    onChange={opt => formik.setFieldValue('subject', opt)}
                    onBlur={formik.handleBlur}
                  />
                </Box>
                <Box flex='1'>
                  <FormLabel ms='4px' fontSize='sm' fontWeight='normal'>ชั้นเรียน</FormLabel>
                  <Select
                    options={CLASS} id='class' name='class' placeholder='เลือกชั้นเรียน'
                    styles={customStyles} value={formik.values.class}
                    onChange={opt => formik.setFieldValue('class', opt)}
                    onBlur={formik.handleBlur}
                  />
                </Box>
              </Flex>
              <FormLabel ms='4px' fontSize='sm' fontWeight='normal'>รายละเอียด</FormLabel>
              <Textarea
                id='description' name='description' fontSize='sm' ms='4px'
                borderRadius='15px' mb='24px' size='lg'
                onChange={formik.handleChange} onBlur={formik.handleBlur}
                value={formik.values.description || ''}
              />
              <FormLabel ms='4px' fontSize='sm' fontWeight='normal'>URL รูปปก</FormLabel>
              <Input
                fontSize='sm' ms='4px' borderRadius='15px' type='text' mb='24px' size='lg'
                name='url_image' onChange={formik.handleChange} onBlur={formik.handleBlur}
                value={formik.values.url_image || ''}
              />
              <FormLabel ms='4px' fontSize='sm' fontWeight='normal'>
                อัพโหลดไฟล์ PDF ใหม่ (ถ้าต้องการเปลี่ยน)
              </FormLabel>
              {formik.values.pdf_file && (
                <Text fontSize='sm' color='gray.500' mb='8px'>
                  ไฟล์ปัจจุบัน: {formik.values.pdf_file}
                </Text>
              )}
              <Input
                fontSize='sm' ms='4px' borderRadius='15px' type='file' mb='24px' size='lg'
                accept='application/pdf'
                onChange={e => formik.setFieldValue('pdf_file_upload', e.currentTarget.files[0])}
              />
              <FormLabel ms='4px' fontSize='sm' fontWeight='normal'>ประเภทใบงาน</FormLabel>
              <Text fontSize='lg' color={textColor} fontWeight='bold' mb='24px'>
                <small>เสียเงิน</small>
                <Switch size='lg' mx='10px' id='is_free' name='is_free' isChecked={formik.values.is_free}
                  onChange={e => formik.setFieldValue('is_free', e.target.checked)} />
                <small>ฟรี</small>
              </Text>
              <Flex gap='5'>
                <Box flex='1'>
                  <FormLabel ms='4px' fontSize='sm' fontWeight='normal'>ราคาเต็ม</FormLabel>
                  <Input
                    fontSize='sm' ms='4px' borderRadius='15px' type='number' mb='24px' size='lg'
                    name='original_price' onChange={formik.handleChange} onBlur={formik.handleBlur}
                    value={formik.values.original_price || 0}
                  />
                </Box>
                <Box flex='1'>
                  <FormLabel ms='4px' fontSize='sm' fontWeight='normal'>ราคาที่ลด</FormLabel>
                  <Input
                    fontSize='sm' ms='4px' borderRadius='15px' type='number' mb='24px' size='lg'
                    name='sale_price' onChange={formik.handleChange} onBlur={formik.handleBlur}
                    value={formik.values.sale_price || 0}
                  />
                </Box>
              </Flex>
              <Flex gap='5' mb='24px'>
                <Text color={textColor}>
                  <Switch mx='8px' id='is_new' isChecked={formik.values.is_new}
                    onChange={e => formik.setFieldValue('is_new', e.target.checked)} />
                  ใหม่ (New)
                </Text>
                <Text color={textColor}>
                  <Switch mx='8px' id='is_recommended' isChecked={formik.values.is_recommended}
                    onChange={e => formik.setFieldValue('is_recommended', e.target.checked)} />
                  แนะนำ (Recommended)
                </Text>
                <Text color={textColor}>
                  <Switch mx='8px' id='is_bestseller' isChecked={formik.values.is_bestseller}
                    onChange={e => formik.setFieldValue('is_bestseller', e.target.checked)} />
                  ขายดี (Bestseller)
                </Text>
              </Flex>
              <Button
                type='submit'
                bg='teal.300' fontSize='1rem' color='white' fontWeight='bold'
                w='100%' h='45' mb='24px'
                isDisabled={!formik.values.name || formik.values.name === ''}
                _hover={{ bg: 'teal.200' }}
                _active={{ bg: 'teal.400' }}
              >
                บันทึก
              </Button>
            </FormControl>
          </CardBody>
        </Card>
      </form>
    </Flex>
  )
}

export default EditWorksheet
